import { useMemo, useState } from 'react'
import clsx from 'clsx'
import price from 'lib/str-formatter/price'
import { Button, Card, Divider, Spin, Text } from 'components/common'
import { BookingsExceedModal, BookingDiscountModal } from '../modals'
import { ellipsisText } from 'utils/custom-functions'
import { DiscountFeeInput, Payment } from 'lib/graphql/storeadmin'
import * as styles from './styles.module.css'

export interface PriceList {
  originalSubTotalPriceTaxExcluded: number
  originalTotalTax: number
  originalSubTotalPrice: number
  originalTotalPrice: number
  subTotal: number
  subTotalPriceTaxExcluded: number
  totalTax: number
  totalPrice: number
  notPay?: number
  discountPrice: number
  specialPriceIncluded?: boolean
}

export interface ExtensionFeeType {
  id: string
  value: number
  reason: string
}

export interface PriceDisplayCardProps {
  discountData?: DiscountFeeInput
  editable?: boolean
  extensionData?: ExtensionFeeType[]
  priceList: PriceList
  title: JSX.Element
  setExceedValue?: (values: [ExtensionFeeType]) => void
  setDiscountValue?: (values: DiscountFeeInput) => void
  subTitle?: JSX.Element
  customerPaidAmount?: Payment
  alreadyPaid?: number
  totalPrice?: number
  onOk?: () => void
  loading: boolean
  isCanceled?: boolean
}

export const PriceDisplayCard = ({
  discountData,
  editable,
  extensionData,
  priceList,
  title,
  customerPaidAmount,
  alreadyPaid,
  totalPrice,
  setExceedValue,
  setDiscountValue,
  subTitle,
  loading,
  isCanceled,
}: PriceDisplayCardProps): JSX.Element => {
  const [showDiscount, setShowDiscount] = useState(false)
  const [showExceed, setShowExceed] = useState(false)

  const checkDueBalance =
    Math.trunc(customerPaidAmount?.amount) !== Math.trunc(totalPrice)

  // 未払い or 過払いのチェック
  // true: 未払い
  // false: 過払い
  const checkNoDueBalance = useMemo(() => {
    return totalPrice - customerPaidAmount?.amount > 0
  }, [totalPrice, customerPaidAmount?.amount])

  return (
    <>
      <Spin spinning={loading} tip="Loading...">
        <Card shadow>
          <div className="grid grid-cols-1 gap-y-2">
            {isCanceled && (
              <p className="text-black font-bold text-2xl">
                このお支払いはキャンセルされました
              </p>
            )}
            <div
              className={`${
                isCanceled && 'bg-gray-100 rounded-lg p-8'
              } grid grid-cols-1 gap-y-2`}
            >
              <div
                className={`${clsx(styles.flex__between__center)} ${
                  isCanceled && 'line-through'
                }`}
              >
                {title}
                {subTitle && subTitle}
              </div>
              <div className={clsx(styles.flex__between)}>
                <div>
                  小計(税込)
                  {priceList?.specialPriceIncluded && `(土日祝日価格を含む)`}
                </div>
                <div>{price(priceList.originalSubTotalPrice)}</div>
              </div>

              <div className={clsx(styles.flex__between)}>
                <div className="flex space-x-1">
                  <div>
                    <div>ディスカウント</div>
                    {discountData?.reason !== '' && (
                      <Text type="secondary">
                        {ellipsisText(discountData?.reason, 30).text}
                      </Text>
                    )}
                  </div>
                  {editable && (
                    <Button
                      type="link"
                      onClick={() => setShowDiscount(true)}
                      style={{ padding: 0, height: 22, fontSize: 12 }}
                    >
                      変更
                    </Button>
                  )}
                </div>
                <div>{price(priceList.discountPrice)}</div>
              </div>

              <div className={clsx(styles.flex__between)}>
                <div className="flex space-x-1">
                  <div>
                    <div>追加料金</div>
                  </div>
                  {editable && (
                    <Button
                      type="link"
                      onClick={() => setShowExceed(true)}
                      style={{ padding: 0, height: 22, fontSize: 12 }}
                    >
                      明細確認・変更
                    </Button>
                  )}
                </div>
                <div>
                  {price(
                    extensionData?.reduce((sum, item) => sum + item.value, 0) ||
                      0
                  )}
                </div>
              </div>

              <div className={clsx(styles.flex__between)}>
                <div>合計(税込)</div>
                <div>{price(priceList.subTotal)}</div>
              </div>
              <div className={clsx(styles.flex__between)}>
                <div>合計(税抜)</div>
                <div>{price(priceList.subTotalPriceTaxExcluded)}</div>
              </div>

              <div className="h-px bg-black opacity-10 w-full my-2" />

              <div className={clsx(styles.flex__between)}>
                <div className="font-bold">請求合計金額</div>
                <div className="font-bold">{price(priceList.totalPrice)}</div>
              </div>

              <div className={clsx(styles.flex__between)}>
                <div>消費税(10%対象)</div>
                <div>({price(priceList.totalTax)})</div>
              </div>

              {!subTitle && (
                <>
                  <Divider />
                  <div className={clsx(styles.flex__between__center)}>
                    <div className={clsx('flex-initial')}>支払い済み</div>
                    {customerPaidAmount ? (
                      <div className={clsx(styles.flex__auto__end)}>
                        {customerPaidAmount
                          ? price(customerPaidAmount.amount)
                          : price(0)}
                      </div>
                    ) : (
                      <div className={clsx(styles.flex__auto__end)}>
                        {alreadyPaid ? price(alreadyPaid) : price(0)}
                      </div>
                    )}
                  </div>
                </>
              )}

              {checkDueBalance && customerPaidAmount && (
                <div
                  className={`
                ${clsx(styles.flex__between__center)}
                ${!checkNoDueBalance && 'text-red-500'}
                ${checkNoDueBalance && 'text-blue-500'}
                font-bold
                `}
                >
                  <div className={clsx('flex-initial')}>
                    {checkNoDueBalance ? '未払い' : '過払い'}
                    <span className="font-normal">
                      （請求合計金額 - 支払い済み）
                    </span>
                  </div>
                  <div
                    className={`
                ${clsx(styles.flex__auto__end)}
                `}
                  >
                    {customerPaidAmount
                      ? price(totalPrice - customerPaidAmount?.amount)
                      : price(totalPrice)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Spin>
      <BookingDiscountModal
        open={showDiscount}
        discountValue={discountData}
        onOk={setDiscountValue}
        onClose={setShowDiscount}
      />
      <BookingsExceedModal
        visible={showExceed}
        exceedValues={extensionData}
        onOk={setExceedValue}
        onClose={setShowExceed}
      />
    </>
  )
}
